// frontend/src/pages/UserProfile.jsx

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PageBanner from '../components/common/PageBanner';
import { ICON, Eye, EyeSlash, Info } from '../icons';
import Tile from '../components/layout/Tile';
import AvatarUpload from '../components/avatarupload/AvatarUpload';
import { useAuth } from '../contexts/AuthContext';
import { useFormatters } from '../hooks/useFormatters';
import { CHART_PALETTES, chartBaseTheme } from '../hooks/useChartTheme';
import useDataTheme from '../hooks/useDataTheme';
import { sessionService } from '../services/SessionApiService';
import { translateRequestError } from '../queries/api';
import { DATA, useDataChanged } from '../queries/dataChanged';
import { userProfile as userProfileKeys } from '../queries/keys';
import { COMMON_TIMEZONES, languageOptions, regionOptions } from '../utils/localeOptions';
import {
  NOTIFICATION_FIELDS, accountPatchBody, extractAccountForm, mergeAccountSave,
} from './userProfileForm';
import { THEME_FAMILIES, themeFamily } from '../theme';
import '../styles/pages.css';
import { showToast } from '../components/common/appToastBus';

const CHART_THEME_OPTIONS = [
  { value: 'ag-sheets',   labelKey: 'userProfile.chartTheme_clean' },
  { value: 'ag-material', labelKey: 'userProfile.chartTheme_modern' },
];

// The live email switches (userProfileForm.NOTIFICATION_FIELDS), each a
// User.email_* column the profile PATCH writes and the mail's sender
// reads (core/email/recipients.py), with the row's label key.
const NOTIFICATION_LABELS = {
  email_stockout_alerts: ['userProfile.notifStockout', 'userProfile.notifStockoutDesc'],
  email_promotion_updates: ['userProfile.notifPromotions', 'userProfile.notifPromotionsDesc'],
};
const NOTIFICATION_SWITCHES = NOTIFICATION_FIELDS.map(
  (field) => ({ field, label: NOTIFICATION_LABELS[field][0], desc: NOTIFICATION_LABELS[field][1] }),
);

// Mails not built yet, shown disabled so the merchant knows what is coming,
// each in the state it is planned to start in.
const COMING_SOON_NOTIFICATIONS = [
  { label: 'userProfile.notifSecurity', desc: 'userProfile.notifSecurityDesc', checked: true },
  { label: 'userProfile.notifMonthly', desc: 'userProfile.notifMonthlyDesc', checked: true },
  { label: 'userProfile.notifUpdates', desc: 'userProfile.notifUpdatesDesc', checked: false },
];

// Strict read before the page shows anything, so a malformed payload is a load
// error rather than a form seeded from it (see userProfileForm.js for why
// nothing here substitutes a blank).
async function fetchUserProfile() {
  try {
    const profile = await sessionService.getUserProfile();
    extractAccountForm(profile);
    return profile;
  } catch (err) {
    console.error('loadUserProfile failed:', err.message);
    throw err;
  }
}

const UserProfile = () => {
  const { t } = useTranslation();
  // Loaded fresh on every visit and refetched only when this page asks for it
  // (after an avatar change): the account form is seeded once from what loads,
  // so a background refetch could not reach it anyway.
  const profileQuery = useQuery({
    queryKey: userProfileKeys.me(),
    queryFn: fetchUserProfile,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (profileQuery.isPending) {
    return (
      <div className="page-container">
        <Stack sx={{ alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Stack>
      </div>
    );
  }

  if (profileQuery.isError) {
    return (
      <div className="page-container">
        <PageBanner severity="error" messages={[translateRequestError(profileQuery.error, t, 'common.fetchError')]} />
      </div>
    );
  }

  return <ProfileSettings userProfile={profileQuery.data} />;
};

const ProfileSettings = ({ userProfile }) => {
  const translation = useTranslation();
  const { t } = translation;
  const queryClient = useQueryClient();
  const changed = useDataChanged();
  const { refreshUser, updateTheme, updateChartTheme, userContext } = useAuth();
  const { formatDate, formatDateTime } = useFormatters();

  const currentChartTheme = userContext.user.chart_theme;

  const currentTheme = useDataTheme();
  const currentFamily = themeFamily(currentTheme);
  const isLightVariant = currentFamily.light === currentTheme;

  // Account form (personal info). Three-axis locale: language drives
  // translations, region_code drives Intl.*
  // formatting, timezone drives timestamp display. Each falls back to
  // Store when empty (server-side via get_effective_*).
  const [accountForm, setAccountForm] = useState(() => extractAccountForm(userProfile));
  const [accountDirty, setAccountDirty] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);

  const unsavedGuard = useUnsavedChangesGuard(accountDirty);

  // Password
  const [passwordForm, setPasswordForm] = useState({
    current_password: '', new_password: '', confirm_password: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  // A person Shopify created signs in from the admin and has no password
  // yet (user-context `has_password`): the Security tile then offers the
  // setup-link mail instead of the change form.
  const hasPassword = userContext.user.has_password;
  const [sendingSetupLink, setSendingSetupLink] = useState(false);


  // Account field change
  const handleAccountChange = (field) => (e) => {
    setAccountForm(prev => ({ ...prev, [field]: e.target.value }));
    setAccountDirty(true);
  };

  // Save account
  const handleSaveAccount = async () => {
    // A submitted email is a REQUEST, not a write: the response carries the
    // unchanged `email` plus the new `pending_email`. So the body says what
    // was asked for, and the response says what happened. Built before the
    // spinner goes on — a throw here would otherwise leave it spinning.
    const body = accountPatchBody(accountForm, userProfile.email);
    setSavingAccount(true);
    try {
      const updated = await sessionService.updateUserProfile(body);
      queryClient.setQueryData(userProfileKeys.me(), (prev) => mergeAccountSave(prev, updated));
      // Reseed from the response, not from the form the merchant typed: the
      // email field must snap back to the address that still signs in.
      setAccountForm(extractAccountForm(updated));
      setAccountDirty(false);
      await refreshUser();
      showToast(body.email
          ? t('userProfile.emailChangeSent', { email: updated.pending_email })
          : t('msg.saveSuccess'), 'success');
    } catch (err) {
      console.error('updateUserProfile failed:', err.message);
      // The email half of this PATCH refuses with typed codes
      // (email_unchanged, pending_email_taken), so the merchant gets the
      // reason rather than one catch-all sentence.
      showToast(translateRequestError(err, t), 'error');
    } finally {
      setSavingAccount(false);
    }
  };

  // Email notification switches: one PATCH per click, the switch stays
  // where the server left it. Only the saved field is merged back, so saves
  // of the two switches in flight at once cannot overwrite each other.
  const handleNotificationToggle = (field) => async (e) => {
    try {
      const updated = await sessionService.updateUserProfile({ [field]: e.target.checked });
      queryClient.setQueryData(userProfileKeys.me(), (prev) => ({ ...prev, [field]: updated[field] }));
      showToast(t('msg.saveSuccess'), 'success');
    } catch (err) {
      console.error('updateUserProfile failed:', err.message);
      showToast(translateRequestError(err, t, 'userProfile.notifSaveFailed'), 'error');
    }
  };

  const handleDiscardAccount = () => {
    // Same strict read as the load — `userProfile` is the payload that already
    // passed it, so this cannot throw.
    setAccountForm(extractAccountForm(userProfile));
    setAccountDirty(false);
  };

  // Timezone uses Autocomplete (free-solo so users can paste any IANA
  // name); plumb its onChange/onInputChange into accountForm.
  const handleTimezoneChange = (_event, value) => {
    setAccountForm(prev => ({ ...prev, timezone: value || '' }));
    setAccountDirty(true);
  };

  // Avatar. <AvatarUpload> owns the upload and the removal gate; this page owns
  // the one surface that speaks to the merchant, so both outcomes land here.
  const handleAvatarChange = async (avatarUrl) => {
    await refreshUser();
    await changed([DATA.profile]);
    showToast(avatarUrl ? t('userProfile.avatarUpdated') : t('userProfile.avatarRemoved'), 'success');
  };

  const handleAvatarError = (message) => {
    showToast(message, 'error');
  };

  // Password
  const handlePasswordChange = async () => {
    setPasswordError('');
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError(t('userProfile.passwordMismatch'));
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError(t('userProfile.passwordTooShort'));
      return;
    }
    setChangingPassword(true);
    try {
      const result = await sessionService.changePassword(passwordForm.current_password, passwordForm.new_password);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      const sessions = result.terminated_sessions || 0;
      const msg = sessions > 0
        ? t('userProfile.passwordChangedWithSessions', { count: sessions })
        : t('userProfile.passwordChanged');
      showToast(msg, 'success');
    } catch (err) {
      console.error('changePassword failed:', err.message);
      setPasswordError(translateRequestError(err, t));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRequestSetupLink = async () => {
    setSendingSetupLink(true);
    try {
      await sessionService.requestSetupLink();
      showToast(t('userProfile.setupLinkSent'), 'success');
    } catch (err) {
      console.error('requestSetupLink failed:', err.message);
      showToast(translateRequestError(err, t), 'error');
    } finally {
      setSendingSetupLink(false);
    }
  };

  const accountActions = accountDirty ? (
    <>
      <Button variant="contained" onClick={handleSaveAccount} loading={savingAccount}>
        {t('btn.saveChanges')}
      </Button>
      <Button variant="outlined" onClick={handleDiscardAccount} disabled={savingAccount}>
        {t('btn.discardChanges')}
      </Button>
    </>
  ) : null;

  return (
    <div className="page-container">
      {unsavedGuard}

      <div className="tiles-row">
        {/* LEFT COLUMN */}
        <div className="user-profile-left-column">

          {/* Account */}
          <Tile title={t('userProfile.accountInfo')} editActions={accountActions}>
            <div className="user-profile-account-section">
              <div className="user-profile-avatar-block">
                <AvatarUpload
                  currentAvatar={userProfile.avatar}
                  onAvatarChange={handleAvatarChange}
                  onError={handleAvatarError}
                  size={80}
                />
              </div>

              <div className="user-profile-account-fields">
                <div className="two-col-row">
                  <div className="two-col-group two-col-group--fill">
                    <Stack spacing={2}>
                      <TextField
                        label={t('userProfile.email')}
                        value={accountForm.email}
                        onChange={handleAccountChange('email')}
                        size="small"
                        fullWidth
                        type="email"
                        autoComplete="email"
                        helperText={t('userProfile.emailHelp')}
                      />
                      {/* A change waiting on its confirmation link. The
                          address above is still the live one — the account
                          only moves when whoever reads mail at the new
                          address opens the link
                          (authentication/services/email_change.py). */}
                      {userProfile.pending_email && (
                        <Alert severity="info" icon={false}>
                          <Typography variant="caption" display="block">
                            {t('userProfile.pendingEmail')}
                          </Typography>
                          <Typography variant="body2">{userProfile.pending_email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('userProfile.pendingEmailNotice', { email: userProfile.pending_email })}
                          </Typography>
                        </Alert>
                      )}
                      <TextField
                        label={t('userProfile.firstName')}
                        value={accountForm.first_name}
                        onChange={handleAccountChange('first_name')}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t('userProfile.lastName')}
                        value={accountForm.last_name}
                        onChange={handleAccountChange('last_name')}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t('userProfile.phone')}
                        value={accountForm.phone}
                        onChange={handleAccountChange('phone')}
                        size="small"
                        fullWidth
                      />
                    </Stack>
                  </div>
                  <div className="two-col-divider" />
                  <div className="two-col-group two-col-group--fill">
                    <Stack spacing={2}>
                      <TextField
                        select
                        label={t('userProfile.language')}
                        value={accountForm.language}
                        onChange={handleAccountChange('language')}
                        size="small"
                        fullWidth
                      >
                        {languageOptions(translation, { storeDefault: true }).map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </TextField>
                      <Autocomplete
                        value={accountForm.timezone || null}
                        onChange={handleTimezoneChange}
                        onInputChange={handleTimezoneChange}
                        options={COMMON_TIMEZONES}
                        freeSolo
                        size="small"
                        fullWidth
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t('userProfile.timezone')}
                          />
                        )}
                      />
                      <TextField
                        select
                        label={t('userProfile.region')}
                        value={accountForm.region_code}
                        onChange={handleAccountChange('region_code')}
                        size="small"
                        fullWidth
                        helperText={t('userProfile.regionDrivesFormatsHelp')}
                      >
                        {regionOptions(translation, { storeDefault: true, current: accountForm.region_code }).map(opt => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                  </div>
                </div>
              </div>
            </div>
          </Tile>

          {/* Appearance */}
          <Tile title={t('userProfile.appearance')}>
            <div className="two-col-row">
              <div className="two-col-group">
                <Typography variant="caption" color="textSecondary">
                  {t('userProfile.theme')}
                </Typography>
                <div className="user-profile-theme-tiles">
                {THEME_FAMILIES.map(family => (
                  <button
                    key={family.key}
                    type="button"
                    className={`user-profile-theme-tile ${currentFamily === family ? 'user-profile-theme-tile-selected' : ''}`}
                    onClick={() => updateTheme(isLightVariant ? family.light : family.dark)}
                  >
                    <div className="user-profile-theme-swatches">
                      {[family.light, family.dark].map((name) => (
                        // data-theme on the preview gives it that theme's own tokens.
                        <div key={name} className="user-profile-theme-mini" data-theme={name}>
                          <div className="user-profile-theme-mini-topbar" />
                          <div className="user-profile-theme-mini-body">
                            <div className="user-profile-theme-mini-sidebar" />
                            <div className="user-profile-theme-mini-content">
                              <div className="user-profile-theme-mini-card" />
                              <div className="user-profile-theme-mini-card" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <span className="user-profile-theme-tile-label">{t(`userProfile.theme_${family.key}`)}</span>
                  </button>
                ))}
                </div>
              </div>
              <div className="two-col-divider" />
              <div className="two-col-group">
                <Typography variant="caption" color="textSecondary">
                  {t('userProfile.chartTheme')}
                </Typography>
                <div className="user-profile-theme-tiles">
                {CHART_THEME_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`user-profile-theme-tile ${currentChartTheme === opt.value ? 'user-profile-theme-tile-selected' : ''}`}
                    onClick={() => updateChartTheme(opt.value)}
                  >
                    <div className="user-profile-chart-theme-swatches">
                      {CHART_PALETTES[chartBaseTheme(opt.value, currentTheme)].fills.slice(0, 6).map((c, i) => (
                        <div key={i} className="user-profile-chart-theme-swatch" style={{ background: c }} />
                      ))}
                    </div>
                    <span className="user-profile-theme-tile-label">{t(opt.labelKey)}</span>
                  </button>
                ))}
                </div>
              </div>
            </div>
          </Tile>

          {/* Security */}
          <Tile title={t('userProfile.security')}>
            {!hasPassword ? (
              <div className="user-profile-password-grid">
                <Typography variant="body2">{t('userProfile.noPasswordYet')}</Typography>
                <div className="user-profile-password-action">
                  <Button
                    variant="outlined"
                    onClick={handleRequestSetupLink}
                    loading={sendingSetupLink}
                  >
                    {t('userProfile.emailMeLink')}
                  </Button>
                </div>
              </div>
            ) : (
            <div className="user-profile-password-grid">
              <TextField
                label={t('userProfile.currentPassword')}
                type={showCurrentPassword ? 'text' : 'password'}
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                size="small"
                fullWidth
                autoComplete="new-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton size="small" onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                        {showCurrentPassword ? <EyeSlash size={ICON.xl} /> : <Eye size={ICON.xl} />}
                      </IconButton>
                    ),
                  },
                }}
              />
              <TextField
                label={t('userProfile.newPassword')}
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                size="small"
                fullWidth
                autoComplete="new-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton size="small" onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                        {showNewPassword ? <EyeSlash size={ICON.xl} /> : <Eye size={ICON.xl} />}
                      </IconButton>
                    ),
                  },
                }}
              />
              <TextField
                label={t('userProfile.confirmPassword')}
                type={showNewPassword ? 'text' : 'password'}
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                size="small"
                fullWidth
                autoComplete="new-password"
              />
              {passwordError && (
                <Alert severity="error">{passwordError}</Alert>
              )}
              <div className="user-profile-password-action">
                <Button
                  variant="outlined"
                  onClick={handlePasswordChange}
                  disabled={!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
                  loading={changingPassword}
                >
                  {t('userProfile.changePasswordBtn')}
                </Button>
              </div>
            </div>
            )}
          </Tile>
        </div>

        {/* RIGHT COLUMN */}
        <div className="user-profile-right-column">

          {/* Account Information */}
          <Tile title={t('userProfile.accountInformation')}>
            <div className="user-profile-readonly-list">
              <div className="user-profile-readonly-item">
                {/* The address that signs in TODAY. An edit above never
                    moves it; only the confirmation link does. */}
                <Typography variant="caption">{t('userProfile.signInEmail')}</Typography>
                <Typography variant="body2">{userProfile.email}</Typography>
              </div>
              <div className="user-profile-readonly-item">
                <Typography variant="caption">{t('userProfile.organization')}</Typography>
                <Typography variant="body2">{userProfile.organization_info?.name || '—'}</Typography>
              </div>
              <div className="user-profile-readonly-item">
                <Typography variant="caption">{t('userProfile.lastSignIn')}</Typography>
                <Typography variant="body2">{userProfile.last_login ? formatDateTime(userProfile.last_login) : '—'}</Typography>
              </div>
              <div className="user-profile-readonly-item">
                <Typography variant="caption">{t('userProfile.accountCreated')}</Typography>
                <Typography variant="body2">{userProfile.date_joined ? formatDate(userProfile.date_joined) : '—'}</Typography>
              </div>
            </div>
          </Tile>

          {/* Email Notifications */}
          <Tile title={t('userProfile.emailNotifications')}>
            <Stack spacing={0}>
              {/* The two live switches: each saves on its own click
                  (User.email_* columns, read by the mails' senders). */}
              {NOTIFICATION_SWITCHES.map(({ field, label, desc }) => (
                <FormControlLabel
                  key={field}
                  control={(
                    <Checkbox
                      size="small"
                      checked={userProfile[field]}
                      onChange={handleNotificationToggle(field)}
                    />
                  )}
                  label={
                    <Stack spacing={0}>
                      <Typography variant="body2">{t(label)}</Typography>
                      <Typography variant="caption" color="text.secondary">{t(desc)}</Typography>
                    </Stack>
                  }
                  sx={{ mx: 1, py: 1, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
                />
              ))}

              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', px: 2, py: 1, bgcolor: 'var(--color-fill)', borderRadius: 1, mx: 1, mt: 1 }}>
                <Info size={ICON.md} />
                <Typography variant="caption" color="text.secondary">
                  {t('userProfile.notifComingSoon')}
                </Typography>
              </Stack>

              {COMING_SOON_NOTIFICATIONS.map(({ label, desc, checked }) => (
                <FormControlLabel
                  key={label}
                  control={<Checkbox checked={checked} disabled size="small" />}
                  label={
                    <Stack spacing={0}>
                      <Typography variant="body2">{t(label)}</Typography>
                      <Typography variant="caption" color="text.secondary">{t(desc)}</Typography>
                    </Stack>
                  }
                  sx={{ mx: 1, py: 1, alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
                />
              ))}
            </Stack>
          </Tile>

        </div>
      </div>

    </div>
  );
};

export default UserProfile;
